import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import {
  Briefcase,
  Plus,
  Trash,
  PencilSimple,
  X,
  FloppyDisk,
  CheckCircle,
  Clock,
  Pause,
  Archive,
  Circle,
  ArrowRight,
  MagnifyingGlass,
  FunnelSimple
} from '@phosphor-icons/react';

const API = `${window.location.origin}/api`;

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Circle },
  { value: 'on_hold', label: 'On Hold', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Pause },
  { value: 'completed', label: 'Completed', color: 'bg-blue-50 text-navy border-blue-200', icon: CheckCircle },
  { value: 'archived', label: 'Archived', color: 'bg-slate-50 text-slate-500 border-slate-200', icon: Archive },
];

const STATUS_MAP = Object.fromEntries(STATUS_OPTIONS.map(s => [s.value, s]));

const COLOR_OPTIONS = ['#1E3A8A', '#059669', '#D97706', '#DC2626', '#7C3AED', '#0891B2', '#BE185D', '#4B5563'];

function MatterForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: initial?.name || '',
    description: initial?.description || '',
    client: initial?.client || '',
    status: initial?.status || 'active',
    color: initial?.color || '#1E3A8A',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await onSave({
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        client: form.client.trim() || undefined,
        status: form.status,
        color: form.color,
      });
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-blue-200 rounded-lg p-5 space-y-4" data-testid="matter-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">Matter Name *</label>
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus
            className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 w-full text-sm text-slate-900 focus:border-navy focus:ring-2 focus:ring-blue-100"
            data-testid="matter-form-name" placeholder="e.g., CAC Annual Return — Quorum Foods, NNPC Contract Review" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">Client / Contact</label>
          <input type="text" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })}
            className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 w-full text-sm text-slate-900 focus:border-navy focus:ring-2 focus:ring-blue-100"
            data-testid="matter-form-client" placeholder="e.g., Dangote Group, MTN Nigeria" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1.5">Description</label>
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2}
          className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 w-full text-sm text-slate-900 resize-none focus:border-navy focus:ring-2 focus:ring-blue-100"
          data-testid="matter-form-description" placeholder="Brief description or scope..." />
      </div>
      <div className="flex items-end gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">Status</label>
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
            className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-900"
            data-testid="matter-form-status">
            {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">Color</label>
          <div className="flex gap-1.5">
            {COLOR_OPTIONS.map(c => (
              <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                className={`w-7 h-7 rounded-md border-2 transition-all ${form.color === c ? 'border-slate-900 scale-110' : 'border-transparent hover:border-slate-300'}`}
                style={{ backgroundColor: c }} data-testid={`color-${c}`} />
            ))}
          </div>
        </div>
      </div>
      <div className="flex gap-2 pt-2">
        <button type="submit" disabled={saving || !form.name.trim()}
          className="bg-navy text-white hover:bg-navy-hover font-medium px-4 py-2.5 rounded-lg text-sm disabled:opacity-50 flex items-center gap-1.5 transition-all duration-200"
          data-testid="matter-form-save">
          <FloppyDisk size={14} weight="fill" />{saving ? 'Saving...' : initial ? 'Update' : 'Create Matter'}
        </button>
        <button type="button" onClick={onCancel}
          className="text-slate-600 border border-slate-200 hover:bg-slate-50 font-medium px-4 py-2.5 rounded-lg text-sm">Cancel</button>
      </div>
    </form>
  );
}

function Matters({ user }) {
  const navigate = useNavigate();
  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchMatters = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const res = await axios.get(`${API}/matters`, { params, withCredentials: true });
      setMatters(res.data);
    } catch (err) {
      console.error('Failed to fetch matters:', err);
    } finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { fetchMatters(); }, [fetchMatters]);

  const resetForm = () => { setShowForm(false); setEditingItem(null); };

  const createMatter = async (payload) => {
    await axios.post(`${API}/matters`, payload, { withCredentials: true });
    resetForm();
    fetchMatters();
  };

  const updateMatter = async (payload) => {
    await axios.put(`${API}/matters/${editingItem.matter_id}`, payload, { withCredentials: true });
    resetForm();
    fetchMatters();
  };

  const deleteMatter = async (id) => {
    if (!window.confirm('Delete this matter? Tasks linked to it will be unlinked (not deleted).')) return;
    await axios.delete(`${API}/matters/${id}`, { withCredentials: true });
    fetchMatters();
  };

  const filteredMatters = search.trim()
    ? matters.filter(m => m.name.toLowerCase().includes(search.toLowerCase()) || (m.client || '').toLowerCase().includes(search.toLowerCase()))
    : matters;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header user={user} />
      <main className="max-w-5xl mx-auto p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-slate-900 mb-1">Matters</h1>
            <p className="text-sm md:text-base text-slate-500">Group your tasks and workflows under projects or workstreams</p>
          </div>
          <button onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-navy text-white hover:bg-navy-hover transition-all duration-200 shadow-sm hover:shadow-md font-medium px-4 py-2 rounded-lg flex items-center gap-2"
            data-testid="create-matter-btn">
            <Plus size={20} weight="bold" />
            <span className="hidden sm:inline">New Matter</span>
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search matters..."
              className="bg-white border border-slate-200 rounded-lg pl-9 pr-4 py-2.5 w-full text-sm text-slate-900 focus:border-navy focus:ring-2 focus:ring-blue-100"
              data-testid="matter-search" />
          </div>
          <div className="flex items-center gap-2">
            <FunnelSimple size={16} className="text-slate-400" />
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700"
              data-testid="matter-status-filter">
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>

        {/* Create/Edit Form */}
        <AnimatePresence>
          {showForm && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-5 overflow-hidden">
              <MatterForm initial={editingItem} onSave={editingItem ? updateMatter : createMatter} onCancel={resetForm} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Matter List — Grid Borders micro-theme */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-navy border-t-transparent" />
          </div>
        ) : filteredMatters.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center" data-testid="no-matters">
            <Briefcase size={40} className="text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-400">{matters.length === 0 ? 'No matters yet. Create your first one to organize your work.' : 'No matching matters.'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="matters-grid">
            {filteredMatters.map((matter) => {
              const statusInfo = STATUS_MAP[matter.status] || STATUS_MAP.active;
              const StatusIcon = statusInfo.icon;
              const progress = matter.task_count > 0 ? Math.round((matter.completed_count / matter.task_count) * 100) : 0;

              return (
                <motion.div key={matter.matter_id}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-sm hover:-translate-y-[1px] transition-all duration-200 cursor-pointer group"
                  onClick={() => navigate(`/matters/${matter.matter_id}`)}
                  data-testid={`matter-card-${matter.matter_id}`}
                >
                  {/* Color bar */}
                  <div className="h-1" style={{ backgroundColor: matter.color || '#1E3A8A' }} />

                  <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 text-sm truncate mb-1">{matter.name}</h3>
                        {matter.client && (
                          <p className="text-xs text-navy font-medium">{matter.client}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={(e) => { e.stopPropagation(); setEditingItem(matter); setShowForm(true); }}
                          className="text-slate-300 hover:text-navy transition-colors p-1"
                          data-testid={`edit-matter-${matter.matter_id}`}>
                          <PencilSimple size={14} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); deleteMatter(matter.matter_id); }}
                          className="text-slate-300 hover:text-red-500 transition-colors p-1"
                          data-testid={`delete-matter-${matter.matter_id}`}>
                          <Trash size={14} />
                        </button>
                      </div>
                    </div>

                    {matter.description && (
                      <p className="text-xs text-slate-500 mb-3 line-clamp-2">{matter.description}</p>
                    )}

                    {/* Progress bar */}
                    {matter.task_count > 0 && (
                      <div className="mb-3">
                        <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                          <span>{matter.completed_count}/{matter.task_count} tasks</span>
                          <span>{progress}%</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-300" style={{ width: `${progress}%`, backgroundColor: matter.color || '#1E3A8A' }} />
                        </div>
                      </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between">
                      <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium border ${statusInfo.color}`}>
                        <StatusIcon size={12} weight="fill" />
                        {statusInfo.label}
                      </span>
                      <span className="text-xs text-slate-300 flex items-center gap-1 group-hover:text-navy transition-colors">
                        View <ArrowRight size={12} />
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

export default Matters;
